import './WebHitList.css'
import { useApi } from '@/hooks/useApi'
import { routes } from '@/utilities/routes'
import { useEffect, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react'
import { websiteHitQuerySpec, type WebsiteHitRow } from 'aevocam-rest-api-spec'
import { BasicPage } from '@/components/layout/BasicPage'
import { Button } from '@/components/action/Button'
import { TextBox } from '@/components/scalar/TextBox'
import { Spinner } from '@/components/graphics/Spinner'
import { Sparkline } from '@/components/graphics/Sparkline'
import { Tooltip } from '@/components/action/Tooltip'
import { DataGrid, type DataGridColumn } from '@/components/dataGrid/DataGrid'
import { DataPager } from '@/components/dataGrid/DataPager'
import {
  doneLoading,
  startLoading,
  type DataGridState,
} from '@/components/dataGrid/createDataGridState'
import type { GlobalStateSetter } from '@/utilities/createGlobalState'
import { formatDecimal } from '@/utilities/formatters'
import { formatSparklineTooltip } from '../telemetry/telemetryHelpers'
import {
  buildWebHitListSummary,
  defaultWebHitListFilters,
  formatDurationMs,
  formatWebHitTimestamp,
  WEB_HIT_DAILY_PERIOD_SEC,
  WEB_HIT_DEFAULT_PAGE_SIZE,
  WEB_HIT_TIME_ZONE,
  webHitListCriteria,
  type WebHitListFilters,
  type WebHitListSummary,
} from './webHitHelpers'

type WebHitGridRow = WebsiteHitRow & { rowKey: string }

function toGridRows(rows: WebsiteHitRow[]): WebHitGridRow[] {
  return rows.map((row, index) => ({
    ...row,
    rowKey: row.id != null ? String(row.id) : `${row.createdAt ?? 'row'}-${index}`,
  }))
}

export function WebHitList() {

  const api = useApi({ requireLogin: true, adminOnly: true })

  const [filters, setFilters] = useState<WebHitListFilters>(defaultWebHitListFilters)
  const [appliedFilters, setAppliedFilters] = useState(defaultWebHitListFilters)
  const [gridState, setGridState] = useState<DataGridState<WebHitGridRow>>({
    rows: [],
    totalRows: 0,
    skip: 0,
    take: WEB_HIT_DEFAULT_PAGE_SIZE,
  })
  const [summary, setSummary] = useState<WebHitListSummary | null>(null)

  const setField = <K extends keyof WebHitListFilters>(
    key: K,
    value: WebHitListFilters[K],
  ) => setFilters(prev => ({ ...prev, [key]: value }))

  const handleFilterEscape = <K extends keyof WebHitListFilters>(key: K) => {
    return (ev: KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
      if (ev.key !== 'Escape') return
      ev.preventDefault()
      setField(key, defaultWebHitListFilters()[key])
    }
  }

  //--------------------------------------------------------------------------------
  useEffect(() => {
    async function fn() {
      startLoading(setGridState, gridState.refreshQuietly)
      try {
        const refreshIncrement = gridState.refresh ?? 0
        const { from, to, common } = webHitListCriteria(appliedFilters)

        const res = await api.request(websiteHitQuerySpec, {
          body: {
            timezone: WEB_HIT_TIME_ZONE,
            queries: {
              rows: {
                ...common,
                aggregatePer: 'none',
                newestFirst: true,
                limit: gridState.take,
                offset: gridState.skip,
              },
              daily: {
                ...common,
                aggregatePer: 'day',
              },
              stats: {
                ...common,
                aggregatePer: 'all',
                withDurationStats: true,
              },
            },
          },
        })

        const results = res.data?.results ?? {}
        const dailyRows = (results.daily ?? []) as WebsiteHitRow[]
        const statsRow = (results.stats ?? [])[0] as WebsiteHitRow | undefined
        const hitRows = (results.rows ?? []) as WebsiteHitRow[]

        setGridState(prev => {
          if (prev.refresh !== refreshIncrement) return prev
          return {
            ...prev,
            rows: toGridRows(hitRows),
            totalRows: statsRow?.count ?? hitRows.length,
          }
        })
        setSummary(buildWebHitListSummary(statsRow, dailyRows, from, to))
      } catch (error) {
        console.error(error)
      } finally {
        doneLoading(setGridState)
      }
    }
    void fn()
  }, [
    appliedFilters,
    gridState.skip,
    gridState.take,
    gridState.refresh,
    api,
  ])

  const handleSubmit = (ev: FormEvent) => {
    ev.preventDefault()
    setAppliedFilters(filters)
    setGridState(prev => prev.skip === 0 ? prev : { ...prev, skip: 0 })
  }

  //--------------------------------------------------------------------------------
  return (
    <BasicPage
      title='Web hits'
      className='WebHitList'
      toolbarButtons={<>
        <Button
          label='Admin'
          icon='chevronLeft'
          href={routes.admin()}
        />
      </>}
    >

      <form className='Cutout Filters' onSubmit={handleSubmit}>

        <div className='FlexRow FlexWrap ItemsEnd Gap3'>

          <TextBox
            label='From'
            type='date'
            value={filters.fromDate}
            onChange={ev => setField('fromDate', ev.target.value)}
            onKeyDown={handleFilterEscape('fromDate')}
            width='10rem'
          />

          <TextBox
            label='To'
            type='date'
            value={filters.toDate}
            onChange={ev => setField('toDate', ev.target.value)}
            onKeyDown={handleFilterEscape('toDate')}
            width='10rem'
          />

          <TextBox
            label='Path'
            placeholder='/pricing*'
            value={filters.path}
            onChange={ev => setField('path', ev.target.value)}
            onKeyDown={handleFilterEscape('path')}
            width='8rem'
          />

          <TextBox
            label='Query'
            placeholder='*ref=email*'
            value={filters.query}
            onChange={ev => setField('query', ev.target.value)}
            onKeyDown={handleFilterEscape('query')}
            width='8rem'
          />

          <TextBox
            label='Address'
            placeholder='10.0.*'
            value={filters.address}
            onChange={ev => setField('address', ev.target.value)}
            onKeyDown={handleFilterEscape('address')}
            width='8rem'
          />

          <TextBox
            label='Agent'
            placeholder='Mozilla*'
            value={filters.agent}
            onChange={ev => setField('agent', ev.target.value)}
            onKeyDown={handleFilterEscape('agent')}
            width='8rem'
          />

          <TextBox
            label='Status'
            inputMode='numeric'
            placeholder='200'
            value={filters.status}
            onChange={ev => setField('status', ev.target.value)}
            onKeyDown={handleFilterEscape('status')}
            width='5rem'
          />

          <TextBox
            label='Dur. >'
            suffix='ms'
            inputMode='numeric'
            placeholder='50'
            value={filters.duration}
            onChange={ev => setField('duration', ev.target.value)}
            onKeyDown={handleFilterEscape('duration')}
            width='6rem'
          />

          <Button
            variant='primary'
            label='Apply'
            icon='check'
            type='submit'
            busy={gridState.loading}
          />

        </div>

      </form>

      <div className='Results'>

        <div className='Cutout Summary'>

          <div className='Stats'>
            <div className='Stat'>
              <span className='StatValue'>
                {summary ? formatDecimal(summary.totalRows) : '—'}
              </span>
              <span className='StatLabel'>total rows</span>
            </div>
            <div className='Stat'>
              <span className='StatValue'>
                {formatDurationMs(summary?.durationMinMs) ?? '—'}
              </span>
              <span className='StatLabel'>min duration</span>
            </div>
            <div className='Stat'>
              <span className='StatValue'>
                {formatDurationMs(summary?.durationAvgMs) ?? '—'}
              </span>
              <span className='StatLabel'>avg duration</span>
            </div>
            <div className='Stat'>
              <span className='StatValue'>
                {formatDurationMs(summary?.durationMaxMs) ?? '—'}
              </span>
              <span className='StatLabel'>max duration</span>
            </div>
          </div>

          {summary && summary.dailyBuckets.length > 0 && (
            <Sparkline
              className='DailyChart'
              values={summary.dailyBuckets}
              formatTooltip={index => formatSparklineTooltip(
                index,
                summary.dailyBuckets,
                summary.bucketFrom,
                WEB_HIT_DAILY_PERIOD_SEC,
              )}
            />
          )}

        </div>

        <div className='Cutout GridSection'>

          <div className='PagerRow'>
            <DataPager
              state={gridState}
              setState={setGridState}
              alignRight
            />
          </div>

          {gridState.loading && gridState.rows.length === 0 && (
            <Spinner />
          )}

          {!gridState.loading && gridState.rows.length === 0 && gridState.lastRefreshedAt && (
            <p className='Empty'>No matching web hits.</p>
          )}

          {gridState.lastRefreshedAt && (
            <DataGrid
              state={gridState}
              setState={setGridState as GlobalStateSetter<DataGridState<WebHitGridRow>>}
              idColumn='rowKey'
              columns={columns}
            />
          )}

        </div>

      </div>

    </BasicPage>
  )
}

const columns: DataGridColumn<WebHitGridRow>[] = [
  {
    id: 'createdAt',
    label: 'Time',
    className: () => 'NoWrap',
    render: (value: Date | string | undefined) => formatWebHitTimestamp(value),
  },
  {
    id: 'status',
    label: 'Status',
    align: 'right',
    className: () => 'Monospace',
    render: (value: number | undefined) => value ?? '',
  },
  {
    id: 'durationMs',
    label: 'Duration',
    align: 'right',
    className: () => 'NoWrap',
    render: (value: number | undefined) => formatDurationMs(value) ?? '',
  },
  {
    id: 'path',
    label: 'Path',
    className: () => 'Ellipsis',
    style: () => ({ maxWidth: '18vw' }),
    render: (value: string | undefined) => renderEllipsisCell(value),
  },
  {
    id: 'query',
    label: 'Query',
    className: () => 'Ellipsis',
    style: () => ({ maxWidth: '18vw' }),
    render: (value: string | undefined) => renderEllipsisCell(value),
  },
  {
    id: 'remoteAddress',
    label: 'Address',
    className: () => 'NoWrap Monospace',
  },
  {
    id: 'userAgent',
    label: 'Agent',
    className: () => 'Ellipsis',
    render: (value: string | undefined) => renderEllipsisCell(value),
  },
]

function renderEllipsisCell(value: string | undefined): ReactNode {
  if (!value) return ''
  return (<>
    <Tooltip>{value}</Tooltip>
    {value}
  </>)
}
